import { createContext, lazy, useContext, useMemo, useReducer } from "react";
import { Item, Partial } from "../type";
import {
  LayoutFrameBuilderProps,
  PageLayout,
} from "../containers/LayoutFrameBuilder";

import {
  initialLayoutFrameBuilder,
  LayoutFrameBuilderActions,
  layoutFrameBuilderReducer,
} from "./layoutFrameBuilderReducer";
import { FlexProps } from "@chakra-ui/react";

interface LayoutFrameBuilderContextState
  extends LayoutFrameBuilderProviderProps {
  zoom: number;
  itemSelected: Item | null;
  isDisabled?: boolean;
}

const LayoutFrameBuilder = lazy(
  () => import("../containers/LayoutFrameBuilder")
);

const LayoutFrameBuilderContext = createContext<LayoutFrameBuilderContextState>(
  {} as any
);

interface LayoutFrameBuilderProviderProps extends LayoutFrameBuilderProps {
  partials: Record<string, Record<string, Record<string, Partial>>>;
  isLoading?: boolean;
  isUploading?: boolean;
  items: Item[];
  isDisabled?: boolean;
  itemSelectedId: string | null;

  onCreate(item: Item): void;
  onChange(item: Item): void;
  onDelete(id: string): void;
  onSelect(id: null | string): void;
}
const LayoutFrameBuilderDispatchContext = createContext(
  (() => {}) as React.Dispatch<LayoutFrameBuilderActions>
);

export const LayoutFrameBuilderProvider: React.FC<
  LayoutFrameBuilderProviderProps & {
    pageLayout: PageLayout;
    containerProps?: FlexProps;
  }
> = ({
  partials,
  items,
  onCreate,
  onDelete,
  onChange,
  onSelect,
  itemSelectedId,
  isDisabled,
  isLoading,
  isUploading,
  ...other
}) => {
  const [state, dispatch] = useReducer(
    layoutFrameBuilderReducer,
    initialLayoutFrameBuilder
  );

  const itemSelected = useMemo(() => {
    return itemSelectedId
      ? items.find((item) => item.id === itemSelectedId) || null
      : null;
  }, [itemSelectedId, items]);

  return (
    <LayoutFrameBuilderContext.Provider
      value={{
        ...state,
        partials,
        items,
        onCreate,
        onDelete,
        onChange,
        onSelect,
        itemSelectedId,
        itemSelected,
        isDisabled,
        isLoading,
        isUploading,
      }}
    >
      <LayoutFrameBuilderDispatchContext.Provider value={dispatch}>
        <LayoutFrameBuilder {...other} />
      </LayoutFrameBuilderDispatchContext.Provider>
    </LayoutFrameBuilderContext.Provider>
  );
};

export const useLayoutFrameBuilderContext = () => {
  return useContext(LayoutFrameBuilderContext);
};

export const useLayoutFrameBuilderDispatchContext = () => {
  return useContext(LayoutFrameBuilderDispatchContext);
};
